import {useReducer} from 'react'

import type {ParseError} from '../helpers/excelParser'
import type {CreateRiaRequest, ImportDuplicateCheckResponseDto} from '../types'

export type ImportStep = 'upload' | 'preview' | 'submitting'

export type ImportReportState = {
  step: ImportStep
  isLoading: boolean
  parsedRows: CreateRiaRequest[]
  parseErrors: ParseError[]
  excludedRows: Set<number>
  importResultStatus: 'success' | 'error' | null
  importResultMessage: string | null
  upmId: string
  siteName: string
  duplicateImportCheck: ImportDuplicateCheckResponseDto | null
}

export type ImportReportAction =
  | {type: 'START_FILE_PARSE'}
  | {type: 'FILE_PARSED'; parsedRows: CreateRiaRequest[]; parseErrors: ParseError[]}
  | {type: 'FILE_PARSE_FAILED'}
  | {type: 'TOGGLE_ROW'; index: number}
  | {type: 'START_SUBMIT'}
  | {type: 'IMPORT_SUCCESS'; message: string}
  | {type: 'IMPORT_ERROR'; message: string}
  | {type: 'BACK_TO_UPLOAD'}
  | {type: 'BACK_TO_PREVIEW'}
  | {type: 'SET_UPM_ID'; upmId: string}
  | {type: 'SET_SITE_NAME'; siteName: string}
  | {type: 'SET_DUPLICATE_CHECK'; check: ImportDuplicateCheckResponseDto | null}
  | {type: 'RESET'; defaultUpmId: string; defaultSiteName: string}

const createInitialState = (upmId: string, siteName: string): ImportReportState => ({
  step: 'upload',
  isLoading: false,
  parsedRows: [],
  parseErrors: [],
  excludedRows: new Set(),
  importResultStatus: null,
  importResultMessage: null,
  upmId,
  siteName,
  duplicateImportCheck: null
})

export const importReportReducer = (
  state: ImportReportState,
  action: ImportReportAction
): ImportReportState => {
  switch (action.type) {
    case 'START_FILE_PARSE':
      return {...state, isLoading: true}
    case 'FILE_PARSED':
      return {
        ...state,
        isLoading: false,
        parsedRows: action.parsedRows,
        parseErrors: action.parseErrors,
        excludedRows: new Set(),
        step: 'preview'
      }
    case 'FILE_PARSE_FAILED':
      return {...state, isLoading: false}
    case 'TOGGLE_ROW': {
      const next = new Set(state.excludedRows)
      if (next.has(action.index)) {
        next.delete(action.index)
      } else {
        next.add(action.index)
      }
      return {...state, excludedRows: next}
    }
    case 'START_SUBMIT':
      return {...state, step: 'submitting', importResultStatus: null, importResultMessage: null}
    case 'IMPORT_SUCCESS':
      return {...state, importResultStatus: 'success', importResultMessage: action.message}
    case 'IMPORT_ERROR':
      return {...state, importResultStatus: 'error', importResultMessage: action.message}
    case 'BACK_TO_UPLOAD':
      return {...state, step: 'upload'}
    case 'BACK_TO_PREVIEW':
      return {...state, step: 'preview', importResultStatus: null, importResultMessage: null}
    case 'SET_UPM_ID':
      return state.upmId === action.upmId ? state : {...state, upmId: action.upmId}
    case 'SET_SITE_NAME':
      return state.siteName === action.siteName ? state : {...state, siteName: action.siteName}
    case 'SET_DUPLICATE_CHECK':
      return state.duplicateImportCheck === action.check
        ? state
        : {...state, duplicateImportCheck: action.check}
    case 'RESET':
      return createInitialState(action.defaultUpmId, action.defaultSiteName)
  }
}

export const useImportReportState = (externalUpmId = '', externalSiteName = '') =>
  useReducer(importReportReducer, createInitialState(externalUpmId, externalSiteName))
